import { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { LogIn } from "lucide-react";
import { sendAdminNotification, sendVendorNotification } from "@/data/services";

interface ReviewFormProps {
  serviceId: number;
  onReviewSubmitted: () => void;
  isAuthenticated: boolean;
}

export const ReviewForm = ({ serviceId, onReviewSubmitted, isAuthenticated }: ReviewFormProps) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please sign in to submit a review"
      });
      return;
    }

    if (rating === 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select a rating"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      // Get service details for the notification
      const { data: serviceData, error: serviceError } = await supabase
        .from('services')
        .select('name')
        .eq('id', serviceId)
        .single();
      
      if (serviceError) {
        console.error('Error fetching service details:', serviceError);
      }
      
      // Insert the review
      const { error } = await supabase
        .from('reviews')
        .insert({
          service_id: serviceId,
          rating,
          comment: comment.trim() || null,
          customer_id: session?.user?.id || null
        });

      if (error) throw error;

      // Prepare notification data
      const notificationData = {
        serviceName: serviceData?.name || `Service #${serviceId}`,
        userName: session?.user?.user_metadata?.name || session?.user?.email?.split('@')[0] || 'Anonymous',
        userEmail: session?.user?.email,
        details: {
          rating,
          comment: comment.trim() || "No comment provided",
        }
      };

      // Send admin notification about the new review
      console.log("Sending admin notification for review");
      const adminNotificationResult = await sendAdminNotification('review', notificationData);
      console.log("Admin notification for review sent:", adminNotificationResult);
      
      // Send vendor notification about the new review
      console.log("Sending vendor notification for review");
      const vendorNotificationResult = await sendVendorNotification('review', serviceId, {
        reviewRating: rating,
        reviewComment: comment.trim() || "No comment provided",
        customerName: notificationData.userName,
        customerEmail: notificationData.userEmail
      });
      console.log("Vendor notification for review sent:", vendorNotificationResult);
      
      // Create toast message
      let toastTitle = "Review Submitted";
      let toastDescription = "Your review has been submitted. Service rating updated automatically.";
      let toastVariant: "default" | "destructive" | "success" | "info" | "warning" = "default";
      
      if (adminNotificationResult || vendorNotificationResult) {
        toastDescription += " Notifications have been sent.";
      }
      
      toast({
        title: toastTitle,
        description: toastDescription,
        variant: toastVariant,
      });
      
      setRating(0);
      setComment("");
      onReviewSubmitted();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to submit review. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertTitle className="flex items-center gap-2">
            <LogIn className="h-4 w-4" />
            Authentication Required
          </AlertTitle>
          <AlertDescription className="mt-2">
            You need to be signed in to leave a review. Please sign in to share your experience with this service.
          </AlertDescription>
          <div className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => window.location.href = "/auth"} 
              className="bg-white hover:bg-gray-50"
            >
              Sign in to Review
            </Button>
          </div>
        </Alert>
        
        <div className="opacity-60 pointer-events-none">
          <div>
            <label className="block text-sm font-medium mb-2">Rating</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} className="w-8 h-8 text-yellow-400" />
              ))}
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium mb-2">
              Comment (optional)
            </label>
            <Textarea
              placeholder="Share your experience..."
              rows={4}
              disabled
            />
          </div>

          <Button type="button" disabled className="mt-4">
            Sign in to Submit Review
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">Rating</label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              className="text-yellow-400 hover:scale-110 transition-transform"
            >
              <Star
                className={`w-8 h-8 ${
                  (hoverRating ? star <= hoverRating : star <= rating)
                    ? "fill-yellow-400"
                    : "fill-transparent"
                }`}
              />
            </button>
          ))}
        </div>
      </div>
      
      <div>
        <label htmlFor="comment" className="block text-sm font-medium mb-2">
          Comment (optional)
        </label>
        <Textarea
          id="comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Share your experience..."
          rows={4}
        />
      </div>

      <Button type="submit" disabled={isSubmitting || rating === 0}>
        {isSubmitting ? "Submitting..." : "Submit Review"}
      </Button>
    </form>
  );
};
